/* v1.0 Ship Line (snippets/itsfpv-ship-line.liquid, inside the stock block).
   For the visitor's country: what checkout charges for shipping this variant
   (plus the current cart), the free-shipping state of the order, the tax and
   duty status outside the EU, and the delivery dates (the stock block's
   dispatch day + the measured transit range of that method). Layout B4:
   one delivery row (hero line + one or two quieter lines) and the
   withdrawal row.
   Rules: Researches Versand/2026-10-08 Conversion Paket/pdp-versandzeile-regeln.md.
   Data: snippets/itsfpv-shipping-rates (generated from the Shopify delivery
   profile, never typed by hand), the same data as the shipping page.

   The core (quote) is pure and also runs in Node: tools/check_logic.mjs. */
(function (root) {
  'use strict';

  // Converted shipping prices round up to whole units; a few currencies to
  // 100 or 1,000 (measured on 460 orders, 2026-10-08; same as the shipping page).
  const ROUNDING_UNIT = { HUF: 100, JPY: 100, KZT: 100, KRW: 1000, IDR: 1000 };
  // A value condition in another currency is compared through the storefront
  // rate, which can move before checkout. Inside this band the answer is
  // "unknown", never "fits".
  const BAND = 0.03;
  // The shop currency every storefront rate is relative to.
  const BASE = 'EUR';
  // A cart refresh that takes longer counts as failed: no cart-based claim.
  const CART_TIMEOUT_MS = 6000;
  // Decimal separator that goes with a thousands separator (Shopify's
  // amount formats: 1,234.56 / 1.234,56 / 1 234,56 / 1'234.56).
  const DECIMAL_FOR = { ',': '.', '.': ',', ' ': ',', ' ': ',', ' ': ',', "'": '.' };

  const and = (...xs) => (xs.includes(false) ? false : xs.includes(null) ? null : true);

  // Order value (cents, visitor currency) in a method's currency as [lo, hi]
  // cents, or null when it cannot be converted.
  function valueIn(cents, visitorCurrency, methodCurrency, rate) {
    if (methodCurrency === visitorCurrency) return [cents, cents];
    if (methodCurrency === BASE && rate > 0) {
      const v = cents / rate;
      return [v * (1 - BAND), v * (1 + BAND)];
    }
    return null;
  }

  const atMost = (v, limit) => (!v ? null : v[1] <= limit ? true : v[0] > limit ? false : null);
  const atLeast = (v, limit) => (!v ? null : v[0] >= limit ? true : v[1] < limit ? false : null);
  const toCents = (amount) => Math.round(Number(amount) * 100);

  function daysOf(method, country) {
    return (method.daysBy && method.daysBy[country]) || method.days || null;
  }

  // q: { country, currency, rate, grams, cents, qty, cart }
  //   grams, cents: one unit of this variant (cents in the visitor currency)
  //   cart: null (empty, or nothing that ships) | { cents, grams, currency }
  // Returns one of
  //   { kind: 'none' }                       no zone: say nothing about shipping
  //   { kind: 'unknown', ships }             cannot be decided: no price claim;
  //                                          ships: true only when a method surely
  //                                          qualifies or the zone has Express
  //   { kind: 'nothing' }                    no method at all (checkout cannot ship it)
  //   { kind: 'express', days }              only the carrier-calculated express
  //   { kind: 'price', from, amount, converted, free, freeWithItem, threshold, missing, days }
  function quote(rates, q) {
    const zone = rates.zones.find((z) => z.countries.includes(q.country));
    if (!zone) return { kind: 'none' };
    const cart = q.cart;
    const hasExpress = zone.methods.some((m) => m.live);
    if (cart && cart.currency !== q.currency) return { kind: 'unknown', ships: false };

    // Shopify adds the default package before it checks a weight condition.
    const grams = q.grams * q.qty + (cart ? cart.grams : 0) + (Number(rates.packageGrams) || 0);
    // Value conditions use the subtotal after discounts: cart.total_price.
    const cents = q.cents * q.qty + (cart ? cart.cents : 0);

    const rows = zone.methods.filter((m) => !m.live).map((m) => {
      const value = valueIn(cents, q.currency, m.currency, q.rate);
      const fits = and(
        m.maxGrams ? grams <= m.maxGrams : true,
        m.maxTotal ? atMost(value, toCents(m.maxTotal)) : true,
        m.minTotal ? atLeast(value, toCents(m.minTotal)) : true,
      );
      const free = m.freeOver ? atLeast(value, toCents(m.freeOver)) : false;
      return { m, fits, free, value };
    });
    const price = (r) => (r.free === true ? 0 : Number(r.m.price));
    const sure = rows.filter((r) => r.fits === true && r.free !== null).sort((a, b) => price(a) - price(b));
    const best = sure[0] || null;
    // A method this page cannot decide might be the cheaper answer at
    // checkout: then no price claim at all.
    const open = rows.filter((r) => r.fits === null || (r.fits === true && r.free === null));
    const lowest = (r) => (r.m.freeOver && r.free !== false ? 0 : Number(r.m.price));
    if (open.some((r) => !best || lowest(r) < price(best))) return { kind: 'unknown', ships: !!best || hasExpress };

    if (!best) {
      const express = zone.methods.find((m) => m.live);
      return express ? { kind: 'express', days: daysOf(express, q.country) } : { kind: 'nothing' };
    }

    const out = {
      kind: 'price',
      from: sure.some((r) => r !== best && price(r) > price(best)),
      free: price(best) === 0,
      freeWithItem: false,
      threshold: null,
      missing: null,
      days: daysOf(best.m, q.country),
    };
    if (!out.free) {
      if (best.m.currency === q.currency) {
        out.amount = Number(best.m.price);
        out.converted = false;
      } else if (best.m.currency === BASE && q.rate > 0) {
        // The checkout amount, not an estimate (Ken 2026-10-08): checkout
        // converts at the storefront rate the price above uses (it already
        // holds Shopify's 2 % fee) and rounds up to a whole unit.
        const unit = ROUNDING_UNIT[q.currency] || 1;
        out.amount = Math.ceil(Math.round(Number(best.m.price) * q.rate * 1e6) / 1e6 / unit) * unit;
        out.converted = true;
      } else {
        return { kind: 'unknown', ships: true };
      }
    }

    // Free-shipping incentive, only where the free rate is in the visitor's
    // currency (amounts are exact, nothing converted).
    const freeRow = rows.find((r) => r.m.freeOver && r.fits !== false);
    if (freeRow && freeRow.m.currency === q.currency) {
      const limit = toCents(freeRow.m.freeOver);
      out.threshold = limit / 100;
      if (out.free) {
        out.freeWithItem = !!cart && cart.cents < limit;
      } else if (cart) {
        out.missing = (limit - cents) / 100;
      }
    }
    return out;
  }

  // Money in the theme's own format without the currency code (Ken
  // 2026-10-08): parsed from Liquid's `money` output for 1,234,567.89 in the
  // visitor currency, so symbol, separators and their order match the price
  // above; whole amounts without ",00" ("€149", "CHF 10", "Lek 1,123").
  function moneyFormatter(sample, lang, currency) {
    const m = /^([^\d]*)(\d(?:[\d.,'\s  ]*\d)?)([^\d]*)$/.exec((sample || '').trim());
    if (m) {
      const [, prefix, core, suffix] = m;
      const digits = core.replace(/\D/g, '');
      const decimals = digits === '123456789' ? 2 : digits === '1234568' ? 0 : -1;
      if (decimals >= 0) {
        const head = decimals ? core.slice(0, -3) : core;
        const thou = (head.match(/[^\d]/) || [''])[0];
        // A format without decimals (amount_no_decimals...) has no decimal
        // separator to read; it is the counterpart of the thousands one.
        const dec = decimals ? core.charAt(core.length - 3) : (DECIMAL_FOR[thou] || '.');
        return (value) => {
          // Never round cents away (Astra 2026-10-09): €5.79 stays "€5,79"
          // even in a no-decimals format; whole amounts drop ",00".
          const [int, frac] = Number(value).toFixed(2).split('.');
          const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
          return prefix + grouped + (frac !== '00' ? dec + frac : '') + suffix;
        };
      }
    }
    return (value) => {
      const opts = { style: 'currency', currency };
      if (Number.isInteger(Number(value))) opts.minimumFractionDigits = 0;
      return new Intl.NumberFormat(lang, opts).format(value);
    };
  }

  // ---------- delivery dates (pure) ----------

  const isoOf = (d) => d.toISOString().slice(0, 10);

  // Delivery days as measured: Monday to Saturday (label to delivered,
  // Sundays not counted). Shop holidays are skipped too, which only ever
  // moves the estimate later.
  function addDeliveryDays(iso, n, holidays = []) {
    const d = new Date(`${iso}T12:00:00Z`);
    let counted = 0;
    let guard = 60;
    while (counted < n && guard-- > 0) {
      d.setUTCDate(d.getUTCDate() + 1);
      if (d.getUTCDay() !== 0 && !holidays.includes(isoOf(d))) counted++;
    }
    return isoOf(d);
  }

  // [first, last] delivery day for a dispatch day and a measured [p20, p80]
  // range. Never a single date: no range, no estimate.
  function arrivalRange(dispatchIso, days, holidays) {
    if (!dispatchIso || !/^\d{4}-\d{2}-\d{2}$/.test(dispatchIso)) return null;
    if (!Array.isArray(days) || !(days[0] >= 1) || !(days[1] > days[0])) return null;
    return [addDeliveryDays(dispatchIso, days[0], holidays), addDeliveryDays(dispatchIso, days[1], holidays)];
  }

  // The two ends of the delivery range. Month written out and said once
  // when both days share it; weekday abbreviations only when the range is
  // at most 7 days (a short range reads as days of the week, a long one as
  // dates). Day and month are joined by no-break spaces.
  //   de  "Sa. 10." / "Di. 13. Oktober"        "16." / "24. Oktober"
  //   en  "Sat, October 10" / "Tue, October 13" "October 16" / "24"
  //   fr  "sam. 10" / "mar. 13 octobre"        "16" / "24 octobre" (1er)
  //   it  "sab 10" / "mar 13 ottobre"          "il 16" / "il 24 ottobre" (l'1, l'8, l'11)
  const NBSP = ' ';
  function formatArrival(lang, [fromIso, toIso]) {
    const base = (lang || 'en').slice(0, 2);
    const span = (Date.parse(`${toIso}T12:00:00Z`) - Date.parse(`${fromIso}T12:00:00Z`)) / 86400000;
    const withDays = span <= 7;
    const parts = (iso) => {
      const d = new Date(`${iso}T12:00:00Z`);
      // Weekday and month as they appear inside a date.
      const got = {};
      new Intl.DateTimeFormat(lang, { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'long' })
        .formatToParts(d).forEach((x) => { got[x.type] = x.value; });
      return { wd: got.weekday || '', day: d.getUTCDate(), mon: got.month || '', ym: iso.slice(0, 7) };
    };
    const a = parts(fromIso);
    const b = parts(toIso);
    const same = a.ym === b.ym;
    const wd = (p) => (withDays ? `${p.wd}${NBSP}` : '');
    if (base === 'de') {
      const one = (p, month) => `${wd(p)}${p.day}.${month ? `${NBSP}${p.mon}` : ''}`;
      return [one(a, !same), one(b, true)];
    }
    if (base === 'en') {
      if (withDays) return [`${a.wd},${NBSP}${a.mon}${NBSP}${a.day}`, `${b.wd},${NBSP}${b.mon}${NBSP}${b.day}`];
      return [`${a.mon}${NBSP}${a.day}`, same ? `${b.day}` : `${b.mon}${NBSP}${b.day}`];
    }
    if (base === 'fr') {
      const one = (p, month) => `${wd(p)}${p.day === 1 ? '1er' : p.day}${month ? `${NBSP}${p.mon}` : ''}`;
      return [one(a, !same), one(b, true)];
    }
    if (base === 'it') {
      const art = (n) => (withDays ? `${n}` : [1, 8, 11].includes(n) ? `l'${n}` : `il${NBSP}${n}`);
      const one = (p, month) => `${wd(p)}${art(p.day)}${month ? `${NBSP}${p.mon}` : ''}`;
      return [one(a, !same), one(b, true)];
    }
    const f = (iso) => new Intl.DateTimeFormat(lang, { timeZone: 'UTC', day: 'numeric', month: 'long', ...(withDays ? { weekday: 'short' } : {}) })
      .format(new Date(`${iso}T12:00:00Z`));
    return [f(fromIso), f(toIso)];
  }

  // Shop holidays in the stock block's own format (YYYY-MM-DD, any separator).
  function parseHolidays(str) {
    return String(str || '').replace(/&amp;#1[03];|&#1[03];/g, ',').split(/[\s,]+/).filter((h) => /^\d{4}-\d{2}-\d{2}$/.test(h));
  }

  // One line from its parts: commas between, capital first letter, no full
  // stop (the lines read as labels). A part is a string or { text, em }.
  function line(parts, lang) {
    const list = parts.map((p) => (typeof p === 'string' ? { text: p } : p)).filter((p) => p && p.text);
    if (!list.length) return [];
    list[0] = { ...list[0], text: list[0].text.charAt(0).toLocaleUpperCase(lang) + list[0].text.slice(1) };
    return list;
  }

  const core = { quote, moneyFormatter, valueIn, addDeliveryDays, arrivalRange, formatArrival, parseHolidays, line };
  if (typeof module === 'object' && module.exports) {
    module.exports = core;
    return;
  }
  if (!root.customElements || root.customElements.get('itsfpv-ship-line')) return;

  class ItsfpvShipLine extends HTMLElement {
    connectedCallback() {
      try {
        this.rates = JSON.parse(this.querySelector('[data-ship-rates]').textContent);
        this.data = JSON.parse(this.querySelector('[data-ship-data]').textContent);
      } catch {
        this.hidden = true;
        return;
      }
      this.lang = document.documentElement.lang || 'en';
      this.country = this.dataset.country;
      this.countryName = this.dataset.countryName || this.country;
      this.contactUrl = this.dataset.contactUrl || '';
      this.currency = this.dataset.currency;
      this.tax = this.dataset.tax;
      this.b2b = this.hasAttribute('data-b2b');
      this.sectionId = this.dataset.section;
      this.str = {};
      // `| t` output is HTML-escaped; textContent gives the plain string.
      this.querySelectorAll('[data-str]').forEach((el) => { this.str[el.dataset.str] = el.textContent.trim(); });
      this.money = moneyFormatter(this.querySelector('[data-money]')?.textContent, this.lang, this.currency);
      this.rows = {
        ship: this.querySelector('[data-row="ship"]'),
        withdrawal: this.querySelector('[data-row="withdrawal"]'),
      };
      this.variantId = String(this.data.variant);
      this.cart = this.cartFrom(this.data.cart);

      // The stock block around this element: its dispatch state (day, kind,
      // countdown) and its holidays.
      this.block = this.closest('[data-stock-block]');
      this.holidays = [];
      try {
        const cfg = JSON.parse(this.block.querySelector('[data-stock-config]').textContent);
        this.holidays = parseHolidays(cfg.shipping && cfg.shipping.holidays);
      } catch { /* no holidays known: Sundays only */ }

      this.cleanup = [];
      const listen = (target, type, fn) => {
        if (!target) return;
        target.addEventListener(type, fn);
        this.cleanup.push(() => target.removeEventListener(type, fn));
      };
      // Variant: the theme's own event, and the form's variant input as a
      // fallback (product-info dispatches change on it).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cleanup.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || (this.sectionId && data.sectionId !== this.sectionId)) return;
          this.setVariant(data.variant ? data.variant.id : null);
        }));
        // Cart: drawer, quick add and product form publish cartUpdate. A full
        // cart in the event is used as is; otherwise cart.js is fetched.
        this.cleanup.push(subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.loadCart(event && event.cartData)));
        if (PUB_SUB_EVENTS.quantityUpdate) {
          this.cleanup.push(subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.render()));
        }
      }
      const form = this.sectionId ? document.getElementById(`product-form-${this.sectionId}`) : null;
      const idInput = form && form.querySelector('input[name="id"]');
      listen(idInput, 'change', () => this.setVariant(idInput.value));
      this.qtyInput = this.sectionId ? document.getElementById(`Quantity-${this.sectionId}`) : null;
      listen(this.qtyInput, 'change', () => this.render());
      listen(this.qtyInput, 'input', () => this.render());
      // Back navigation restores a page whose cart may have changed.
      listen(window, 'pageshow', (e) => { if (e.persisted) this.loadCart(); });
      // The stock block states the dispatch day, moves it at the cutoff and
      // counts the minutes down.
      listen(this.block, 'stock-block:dispatch', () => this.render());

      this.render();
    }

    disconnectedCallback() {
      (this.cleanup || []).forEach((fn) => fn && fn());
      this.cleanup = [];
      if (this.block) this.block.classList.remove('has-ship-dispatch');
    }

    t(key, values = {}) {
      return Object.entries(values).reduce((s, [k, v]) => s.split(`[${k}]`).join(v), this.str[key] || '');
    }

    // Only a cart with something that ships changes the answer.
    cartFrom(c) {
      if (!c) return null;
      const ships = typeof c.s === 'number' ? c.s === 1 : !!c.requires_shipping;
      if (!ships) return null;
      return {
        cents: Number(typeof c.p === 'number' ? c.p : c.total_price),
        grams: Number(typeof c.w === 'number' ? c.w : c.total_weight),
        currency: c.c || c.currency,
      };
    }

    setVariant(id) {
      this.variantId = id ? String(id) : null;
      this.render();
    }

    // Only the newest request writes. From the moment a refresh starts the
    // cart is unknown (Astra 2026-10-08): the row keeps its height but shows
    // nothing (render(): blank), so no quote and no dispatch promise for the
    // old cart stays on screen. A failed or slow refresh (6 s) leaves the cart
    // unknown: blank until a valid cart arrives.
    loadCart(cartData) {
      const seq = (this.cartSeq = (this.cartSeq || 0) + 1);
      if (this.cartAbort) this.cartAbort.abort();
      this.cartAbort = null;
      const apply = (cart, ok) => {
        if (seq !== this.cartSeq) return;
        this.cartPending = false;
        this.cartUnknown = !ok;
        this.cart = ok ? this.cartFrom(cart) : null;
        this.render();
      };
      if (cartData && Array.isArray(cartData.items) && typeof cartData.total_weight === 'number') {
        apply(cartData, true);
        return;
      }
      this.cartPending = true;
      this.cartUnknown = true;
      this.cart = null;
      this.render();
      const base = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
      const abort = typeof AbortController === 'function' ? new AbortController() : null;
      this.cartAbort = abort;
      const timer = setTimeout(() => { if (abort) abort.abort(); apply(null, false); }, CART_TIMEOUT_MS);
      fetch(`${base}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: abort ? abort.signal : undefined })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((cart) => apply(cart, true))
        .catch(() => apply(null, false))
        .finally(() => clearTimeout(timer));
    }

    qty() {
      const n = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    rate() {
      const c = root.Shopify && root.Shopify.currency;
      const rate = c && c.active === this.currency ? Number(c.rate) : NaN;
      return rate > 0 ? rate : null;
    }


    // "1 Std. 12 Min." from whole minutes.
    duration(minutes) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      if (h && m) return this.t('time_hm', { h: String(h), m: String(m) });
      return h ? this.t('time_h', { h: String(h) }) : this.t('time_m', { m: String(Math.max(1, m)) });
    }

    // How it ships, from the stock block's own state; null where the stock
    // block states no concrete day (busy mode, custom build, pre-order, sold
    // out, launch, countdown off).
    dispatchState() {
      const d = this.block ? this.block.dataset : {};
      if (!d.dispatchKind || !d.dispatchDate) return null;
      if (d.dispatchKind === 'today') {
        const minutes = parseInt(d.dispatchMinutes, 10);
        if (!(minutes > 0)) return null;
        return { date: d.dispatchDate, today: true, text: this.t('dispatch_today', { time: this.duration(minutes) }) };
      }
      if (d.dispatchKind === 'tomorrow') return { date: d.dispatchDate, text: this.t('dispatch_tomorrow') };
      if (d.dispatchKind === 'day' && d.dispatchDay) return { date: d.dispatchDate, text: this.t('dispatch_day', { day: d.dispatchDay }) };
      return null;
    }

    // Tax and duty status where it changes what the customer pays.
    taxParts(variant, result) {
      if (this.tax === 'import') return [this.t('import_on_delivery')];
      if (this.tax !== 'us') return [];
      // The duty number is for this variant alone: one unit, no cart (the
      // checkout duty would also cover the cart).
      const duty = variant.d;
      if (Number.isInteger(duty) && duty >= 0 && this.qty() === 1 && !this.cart && !this.cartUnknown && result.kind !== 'unknown') {
        return [this.t('us_duty_amount', { amount: this.money(duty / 100) }), this.t('us_nothing_due')];
      }
      return [this.t('us_duty'), this.t('us_nothing_due')];
    }

    write(el, parts) {
      const list = line(parts, this.lang);
      if (!list.length) {
        el.hidden = true;
        el.replaceChildren();
        return;
      }
      const nodes = [];
      list.forEach((p, i) => {
        // A part marked as a sentence follows a full stop and ends with one.
        if (i) nodes.push(document.createTextNode(p.sentence ? ' ' : ', '));
        if (p.href) {
          const a = document.createElement('a');
          a.className = 'ship-line__link';
          a.href = p.href;
          a.textContent = p.text;
          nodes.push(a);
          if (p.sentence) nodes.push(document.createTextNode('.'));
        } else if (p.em) {
          const em = document.createElement('span');
          em.className = 'ship-line__em';
          em.textContent = p.text;
          nodes.push(em);
        } else {
          nodes.push(document.createTextNode(p.text));
        }
      });
      el.replaceChildren(...nodes);
      el.hidden = false;
    }

    render() {
      const variant = this.variantId ? this.data.variants[this.variantId] : null;
      // No line for sold-out, digital, free or unknown variants.
      if (!variant || !variant.a || !variant.s || !(variant.p > 0)) {
        this.hidden = true;
        if (this.block) this.block.classList.remove('has-ship-dispatch');
        return;
      }
      this.hidden = false;
      // The statutory right is a consumer right: not for dealers (B2B), and
      // custom builds may have none. Liquid leaves the row out for B2B too.
      if (this.rows.withdrawal) this.rows.withdrawal.hidden = !!variant.c || this.b2b;
      const row = this.rows.ship;
      if (!row) {
        if (this.block) this.block.classList.remove('has-ship-dispatch');
        return;
      }

      // Cart unknown (a refresh running, failed or timed out): blank, not
      // "last known" (Astra 2026-10-08, second review). No dispatch day, no
      // dates, no price, and not the stock block's own dispatch row either:
      // whether this order ships at all may have changed (FI/SE ten frames).
      // Every row keeps its place (visibility and the reserved height, never
      // hidden or display:none; Ken: buttons never move) until a valid cart
      // arrives. B2B lines never depend on the cart.
      if (this.cartUnknown && !this.b2b) {
        if (!row.hidden && !row.style.minHeight) row.style.minHeight = `${row.offsetHeight}px`;
        row.classList.add('is-pending');
        row.querySelectorAll('[data-hero], [data-sub]').forEach((el) => el.replaceChildren());
        if (this.block) this.block.classList.add('is-ship-unknown');
        this.lastQuote = { country: this.country, currency: this.currency, b2b: this.b2b, dispatch: null, range: null, result: { kind: 'cart_unknown' } };
        return;
      }
      row.style.minHeight = '';
      if (this.block) this.block.classList.remove('is-ship-unknown');

      // B2B: their line prices drop after adding (wholesale discount), so no
      // claim based on the cart or on the order value.
      const cart = this.b2b ? null : this.cart;
      let result = quote(this.rates, {
          country: this.country,
          currency: this.currency,
          rate: this.rate(),
          grams: Number(variant.g) || 0,
          cents: Number(variant.p),
          qty: this.qty(),
          cart,
        });
      if (this.b2b && result.kind === 'price' && result.free) {
        result = { ...result, kind: 'threshold_only' };
      }

      // A dispatch day or delivery dates only when the order certainly ships:
      // a method qualifies, or Express is there. "Unknown" without that
      // certainty (e.g. FI/SE near 999 € in another currency, over 500 g)
      // gets no promise; the cost line is empty there anyway.
      const uncertain = result.kind === 'unknown' && !result.ships;
      const dispatch = uncertain ? null : this.dispatchState();
      const free = result.kind === 'price' && result.free;

      // The cost, as it continues a line ("5,79 €, ab 149 € kostenlos") and
      // as a line of its own ("Versand 5,79 €, ab 149 € kostenlos"). Free
      // shipping is said about the order, never about the item.
      const cost = (own) => {
        if (result.kind === 'express') return [this.t('express_only')];
        if (result.kind === 'threshold_only') return [this.t('free_over', { amount: this.money(result.threshold) })];
        if (result.kind !== 'price') return [];
        if (free) return [this.t(cart && !result.freeWithItem ? 'free_order' : 'free_with_item')];
        const price = this.money(result.amount);
        const parts = [own
          ? this.t(result.from ? 'ship_from' : 'ship', { price })
          : this.t(result.from ? 'cost_from' : 'cost', { price })];
        if (cart && result.missing !== null && result.missing > 0) {
          parts.push({ text: this.t('free_missing', { amount: this.money(result.missing) }), em: true });
        } else if (!cart && result.threshold !== null) {
          parts.push(this.t('free_over', { amount: this.money(result.threshold) }));
        }
        return parts;
      };
      const tax = result.kind === 'none' ? [] : this.taxParts(variant, result);

      const range = dispatch && result.days ? arrivalRange(dispatch.date, result.days, this.holidays) : null;
      // The hero never carries a price: the delivery dates when known,
      // otherwise how it ships; the cost is always a quiet line below.
      let hero = [];
      let subs = [];
      if (dispatch && range) {
        const [from, to] = formatArrival(this.lang, range);
        hero = [this.t(free ? 'hero_free' : 'hero', { range: this.t('range', { from, to }) })];
        // Before the cutoff the countdown takes its own line.
        subs = dispatch.today
          ? [[dispatch.text], [...cost(false), ...tax]]
          : [[dispatch.text, ...cost(false)], tax];
      } else if (dispatch) {
        hero = [dispatch.text];
        subs = [cost(true), tax];
      } else {
        // No dispatch day (custom build, busy mode, pre-order): the stock
        // block's own rows say when; this row carries only the cost.
        subs = [cost(true), tax];
      }
      if (result.kind === 'none') subs = [];
      // Checkout offers no method for this order (SE/FI over 999 € and over
      // the envelope weight): no dispatch day, no dates, one plain sentence
      // with the way out (Astra 2026-10-08).
      const nothing = result.kind === 'nothing';
      if (nothing) {
        hero = [];
        subs = [[{ text: this.t('none', { country: this.countryName }) }, { text: this.t('write_us'), href: this.contactUrl, sentence: true }]];
      }

      // What the line was computed from, for checks in the console.
      this.lastQuote = {
        country: this.country, currency: this.currency, qty: this.qty(),
        itemCents: Number(variant.p), itemGrams: Number(variant.g) || 0,
        cartCents: cart ? cart.cents : null, cartGrams: cart ? cart.grams : null,
        b2b: this.b2b, dispatch, range, result,
      };

      this.write(row.querySelector('[data-hero]'), hero);
      const subEls = row.querySelectorAll('[data-sub]');
      subEls.forEach((el, i) => this.write(el, subs[i] || []));
      row.classList.remove('is-pending');
      const heroless = !line(hero, this.lang).length;
      row.classList.toggle('is-heroless', heroless);
      row.hidden = heroless && !subs.some((part) => line(part || [], this.lang).length);
      const truck = !!dispatch && !nothing;
      row.querySelectorAll('[data-icon]').forEach((svg) => {
        svg.toggleAttribute('hidden', svg.dataset.icon !== (truck ? 'truck' : 'box'));
      });
      // Our row replaces the stock block's dispatch row while it states a
      // day, and hides it when checkout offers no method at all or shipping
      // is not certain (no dispatch promise from either row).
      if (this.block) this.block.classList.toggle('has-ship-dispatch', nothing || uncertain || (!!dispatch && !row.hidden));
    }
  }

  root.customElements.define('itsfpv-ship-line', ItsfpvShipLine);
})(typeof window !== 'undefined' ? window : globalThis);
